import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "../../../../../Components/common/Spinner/Spinner";
import TabButton from "../../../../../Components/common/TabButton/TabButton";
import DetailButton from "../DetectionDetailPopup/DetailButton";
import useDetectionDetailPopup from "../DetectionDetailPopup/useDetectionDetailPopup";
import ProcessDetails from "./ProcessDetails";
import { RequestAgentProcessSnapshot } from "../../../../../store/actions/integrationsActions";

import "./styles.scss";

const MIN_PANE_HEIGHT = 120;
const SPLITTER_HEIGHT = 10;
const DEFAULT_TOP_HEIGHT = 300;
const EMPTY = [];

const normalizePath = (path) =>
  path
    ? String(path)
        .trim()
        .toLowerCase()
        .replace(/[\\/]+/g, "\\")
    : "";

const isSystemPath = (path) => {
  const normalized = normalizePath(path);
  return normalized.startsWith("c:\\windows\\") || normalized.startsWith("\\systemroot\\");
};

const formatSize = (size) => {
  if (size === null || size === undefined || size === "") return "";
  const kb = Number(size) / 1024;
  return `${kb.toLocaleString(undefined, { maximumFractionDigits: 0 })} K`;
};

// Builds the parent/child tree from pid/ppid. Processes whose parent is missing,
// points to itself or forms a cycle (pid reuse) become roots.
const buildTree = (processes) => {
  const byPid = new Map();
  processes.forEach((process) => byPid.set(process.pid, process));

  const children = new Map();
  const roots = [];
  processes.forEach((process) => {
    const parent = byPid.get(process.ppid);
    if (!parent || process.ppid === process.pid) {
      roots.push(process.pid);
      return;
    }
    if (!children.has(process.ppid)) children.set(process.ppid, []);
    children.get(process.ppid).push(process.pid);
  });

  const reachable = new Set();
  const markReachable = (pid) => {
    if (reachable.has(pid)) return;
    reachable.add(pid);
    (children.get(pid) || []).forEach(markReachable);
  };
  roots.forEach(markReachable);

  // Processes only reachable through a cycle: cut the cycle at the first one found
  processes.forEach((process) => {
    if (reachable.has(process.pid)) return;
    const siblings = children.get(process.ppid) || [];
    children.set(
      process.ppid,
      siblings.filter((pid) => pid !== process.pid),
    );
    roots.push(process.pid);
    markReachable(process.pid);
  });

  return { byPid, children, roots };
};

const ProcessTree = ({ id }) => {
  const dispatch = useDispatch();
  const { findProcessDetail, openDetail, popup } = useDetectionDetailPopup();

  const snapshot = useSelector((state) => state.integrations.agentProcessSnapshot);
  const loading = useSelector((state) => state.integrations.agentProcessSnapshotLoading);
  const report = useSelector((state) => state.integrations.agentAnalysisData);

  const [filter, setFilter] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedPid, setSelectedPid] = useState(null);
  const [detailTab, setDetailTab] = useState("Details");
  const [topHeight, setTopHeight] = useState(DEFAULT_TOP_HEIGHT);

  const bodyRef = useRef(null);

  // Selecting a process always opens its Details tab first
  const selectProcess = (pid) => {
    setSelectedPid(pid);
    setDetailTab("Details");
  };

  useEffect(() => {
    if (id) dispatch(RequestAgentProcessSnapshot({ originalId: id }));
  }, [dispatch, id]);

  const processes = snapshot?.processes || EMPTY;
  const sockets = snapshot?.sockets || EMPTY;
  const moduleInfo = snapshot?.module_info || EMPTY;
  const modules = snapshot?.modules || EMPTY;

  const tree = useMemo(() => buildTree(processes), [processes]);

  // Risk level per pid from the analysis results: "threat" for detections and
  // active threats, "suspicious" for unsigned/untrusted or networked processes.
  // The reasons are shown in the row tooltip and the Details tab.
  const { risk: riskByPid, reasons: reasonsByPid } = useMemo(() => {
    const risk = new Map();
    const reasons = new Map();
    const pidsByName = new Map();
    const pidsByPath = new Map();
    processes.forEach((process) => {
      const name = String(process.name || "").toLowerCase();
      if (!pidsByName.has(name)) pidsByName.set(name, []);
      pidsByName.get(name).push(process.pid);
      const path = normalizePath(process.path);
      if (path) {
        if (!pidsByPath.has(path)) pidsByPath.set(path, []);
        pidsByPath.get(path).push(process.pid);
      }
    });

    const mark = (pid, level, reason) => {
      const value = parseInt(pid, 10);
      if (Number.isNaN(value) || !tree.byPid.has(value)) return false;
      if (risk.get(value) !== "threat") risk.set(value, level);
      if (!reasons.has(value)) reasons.set(value, []);
      if (!reasons.get(value).includes(reason)) reasons.get(value).push(reason);
      return true;
    };
    const markByNameOrPath = (name, level, reason) => {
      const path = normalizePath(name);
      const pids = pidsByPath.get(path) || pidsByName.get(path.split("\\").pop()) || [];
      pids.forEach((pid) => mark(pid, level, reason));
    };

    (report?.suspicious_processes || []).forEach((item) => {
      const reason =
        item.source === "sigma"
          ? `Suspicious: signature ${item.signature_status || "missing"} (Sigma)`
          : `Suspicious: ${item.threat_count ?? 0} memory scan finding(s)${
              item.signature_status ? `, signature ${item.signature_status}` : ""
            }`;
      if (!mark(item.pid, "suspicious", reason))
        markByNameOrPath(item.path || item.process_name, "suspicious", reason);
    });
    (report?.detection_details?.processes || []).forEach((item) => {
      const reason = `Detection (${item.engine || "unknown"}): ${item.reason || "-"}${
        item.score !== undefined && item.score !== null ? ` — score ${item.score}` : ""
      }`;
      if (!mark(item.pid, "threat", reason)) markByNameOrPath(item.process, "threat", reason);
    });
    (report?.active_threats?.processes || []).forEach((item) => {
      const reason = `Active threat: ${item.match_reason || "-"}`;
      if (!mark(item.process_id, "threat", reason))
        markByNameOrPath(item.process_path, "threat", reason);
    });

    return { risk, reasons };
  }, [processes, report, tree]);

  const modulesByPid = useMemo(() => {
    const grouped = new Map();
    modules.forEach((module) => {
      if (!grouped.has(module.pid)) grouped.set(module.pid, []);
      grouped
        .get(module.pid)
        .push({ ...(moduleInfo[module.m] || {}), base: module.base, size: module.size });
    });
    return grouped;
  }, [modules, moduleInfo]);

  // Connections per pid: the agent's socket table, plus the connections the report
  // found through Sigma/osquery. Those run earlier in the scan, so short-lived
  // connections (and IPv6 ones from older agents) can be missing from the socket table.
  const socketsByPid = useMemo(() => {
    const grouped = new Map();
    const add = (pid, socket) => {
      if (!grouped.has(pid)) grouped.set(pid, []);
      grouped.get(pid).push(socket);
    };
    sockets.forEach((socket) => add(socket.pid, { ...socket, source: "Socket table" }));

    const addReportConnections = (pid, connections) => {
      const value = parseInt(pid, 10);
      if (Number.isNaN(value) || !connections) return;
      String(connections)
        .split(",")
        .forEach((connection) => {
          const text = connection.trim();
          const separator = text.lastIndexOf(":");
          if (separator <= 0) return;
          const address = text.substring(0, separator);
          const port = text.substring(separator + 1);
          // "0.0.0.0:0", ":::0", "0:0": no remote endpoint, nothing to show
          if (port === "0" && ["0", "0.0.0.0", "::", ""].includes(address)) return;
          const known = (grouped.get(value) || []).some(
            (socket) =>
              socket.remote_address === address && String(socket.remote_port) === String(port),
          );
          if (known) return;
          add(value, { remote_address: address, remote_port: port, source: "Sigma / osquery" });
        });
    };
    (report?.network_connections || []).forEach((item) =>
      addReportConnections(item.pid, item.connections),
    );
    (report?.suspicious_processes || []).forEach((item) =>
      addReportConnections(item.pid, item.connections),
    );

    return grouped;
  }, [sockets, report]);

  const query = filter.trim().toLowerCase();
  const filtering = query.length > 0 || flaggedOnly;

  // While filtering, matching processes stay visible together with their ancestors
  const visiblePids = useMemo(() => {
    if (!filtering) return null;
    const matches = (process) => {
      if (flaggedOnly && !riskByPid.has(process.pid)) return false;
      if (!query) return true;
      return [
        process.name,
        process.path,
        process.cmdline,
        process.description,
        process.company,
        process.user,
        process.pid,
      ].some(
        (value) =>
          value !== null && value !== undefined && String(value).toLowerCase().includes(query),
      );
    };

    const visible = new Set();
    processes.forEach((process) => {
      if (!matches(process)) return;
      let current = process;
      const seen = new Set();
      while (current && !visible.has(current.pid) && !seen.has(current.pid)) {
        seen.add(current.pid);
        visible.add(current.pid);
        current = current.ppid !== current.pid ? tree.byPid.get(current.ppid) : null;
      }
    });
    return visible;
  }, [filtering, query, flaggedOnly, processes, riskByPid, tree]);

  const rows = useMemo(() => {
    const result = [];
    const walk = (pid, depth) => {
      if (visiblePids && !visiblePids.has(pid)) return;
      const childPids = (tree.children.get(pid) || []).filter(
        (childPid) => !visiblePids || visiblePids.has(childPid),
      );
      result.push({ process: tree.byPid.get(pid), depth, hasChildren: childPids.length > 0 });
      if (!filtering && collapsed.has(pid)) return;
      childPids.forEach((childPid) => walk(childPid, depth + 1));
    };
    tree.roots.forEach((pid) => walk(pid, 0));
    return result;
  }, [tree, visiblePids, filtering, collapsed]);

  const toggleCollapsed = (pid) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });

  const collapseAll = () =>
    setCollapsed(
      new Set([...tree.children.entries()].filter(([, c]) => c.length).map(([pid]) => pid)),
    );

  const startResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = topHeight;
    const totalHeight = bodyRef.current ? bodyRef.current.clientHeight : 0;

    const onMove = (moveEvent) => {
      const maxHeight = totalHeight - MIN_PANE_HEIGHT - SPLITTER_HEIGHT;
      const next = startHeight + moveEvent.clientY - startY;
      setTopHeight(Math.max(MIN_PANE_HEIGHT, Math.min(maxHeight, next)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };

    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (loading) {
    return (
      <div className="thunter-process-tree thunter-process-tree--center">
        <Spinner width={24} height={24} />
      </div>
    );
  }

  if (!processes.length) {
    return (
      <div className="thunter-process-tree thunter-process-tree--center">
        <p className="thunter-process-tree__empty">
          Process snapshot is not available for this report. Re-scan the machine with the updated
          agent to see the process tree.
        </p>
      </div>
    );
  }

  const selected = selectedPid !== null ? tree.byPid.get(selectedPid) : null;
  const selectedModules = selected
    ? [...(modulesByPid.get(selected.pid) || [])].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      )
    : [];
  const selectedSockets = selected ? socketsByPid.get(selected.pid) || [] : [];

  const renderDetailPane = () => {
    if (!selected) {
      return (
        <p className="thunter-process-tree__hint">
          Select a process to see its DLLs, network connections and details.
        </p>
      );
    }

    if (detailTab === "DLLs") {
      if (!selectedModules.length) {
        return (
          <p className="thunter-process-tree__hint">
            No loaded modules were collected for this process (access denied or protected process).
          </p>
        );
      }
      return (
        <div className="thunter-process-tree__table thunter-process-tree__table--dlls">
          <div className="thunter-process-tree__head">
            <span>Name</span>
            <span>Description</span>
            <span>Company Name</span>
            <span>Version</span>
            <span>Base</span>
            <span>Size</span>
            <span>Path</span>
          </div>
          {selectedModules.map((module, index) => {
            const unusual = !isSystemPath(module.path) || !module.company;
            return (
              <div
                key={`${module.path}-${index}`}
                className={`thunter-process-tree__row ${unusual ? "thunter-process-tree__row--unusual" : ""}`}
                title={
                  unusual
                    ? "Loaded from outside the Windows directory or has no version info"
                    : undefined
                }>
                <span>{module.name}</span>
                <span>{module.description}</span>
                <span>{module.company}</span>
                <span>{module.version}</span>
                <span className="thunter-process-tree__mono">{module.base}</span>
                <span className="thunter-process-tree__right">{formatSize(module.size)}</span>
                <span title={module.path}>{module.path}</span>
              </div>
            );
          })}
        </div>
      );
    }

    if (detailTab === "Network") {
      if (!selectedSockets.length) {
        return (
          <p className="thunter-process-tree__hint">No network connections for this process.</p>
        );
      }
      return (
        <div className="thunter-process-tree__table thunter-process-tree__table--network">
          <div className="thunter-process-tree__head">
            <span>Protocol</span>
            <span>Local Address</span>
            <span>Remote Address</span>
            <span>State</span>
            <span>Source</span>
          </div>
          {selectedSockets.map((socket, index) => (
            <div key={index} className="thunter-process-tree__row">
              <span>{socket.protocol || "-"}</span>
              <span className="thunter-process-tree__mono">
                {socket.local_address ? `${socket.local_address}:${socket.local_port ?? ""}` : "-"}
              </span>
              <span className="thunter-process-tree__mono">
                {socket.remote_address
                  ? `${socket.remote_address}:${socket.remote_port ?? ""}`
                  : "-"}
              </span>
              <span>{socket.state || "-"}</span>
              <span>{socket.source}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <ProcessDetails
        process={selected}
        parent={tree.byPid.get(selected.ppid)}
        detection={findProcessDetail({ process: selected.name, pid: selected.pid })}
        suspicious={(report?.suspicious_processes || []).find(
          (item) => String(item.pid) === String(selected.pid),
        )}
        reasons={reasonsByPid.get(selected.pid) || []}
        onSelectParent={selectProcess}
        onOpenDetection={openDetail}
      />
    );
  };

  return (
    <div className="thunter-process-tree">
      <div className="thunter-process-tree__toolbar">
        <input
          className="thunter-process-tree__filter"
          placeholder="Filter by process name, path, PID, user, command line, company..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="thunter-process-tree__toggle">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(e) => setFlaggedOnly(e.target.checked)}
          />
          Flagged only
        </label>
        <button
          type="button"
          className="thunter-process-tree__button"
          disabled={filtering}
          onClick={() => setCollapsed(new Set())}>
          Expand all
        </button>
        <button
          type="button"
          className="thunter-process-tree__button"
          disabled={filtering}
          onClick={collapseAll}>
          Collapse all
        </button>
        <span className="thunter-process-tree__summary">
          {filtering ? `${rows.length} / ` : ""}
          {processes.length} processes
        </span>
      </div>

      <div className="thunter-process-tree__body" ref={bodyRef}>
        <div
          className="thunter-process-tree__table thunter-process-tree__table--processes"
          style={{ height: topHeight }}>
          <div className="thunter-process-tree__head">
            <span>Process</span>
            <span className="thunter-process-tree__right">PID</span>
            <span className="thunter-process-tree__right">Threads</span>
            <span>User</span>
            <span>Description</span>
            <span>Company Name</span>
            <span>Path</span>
          </div>
          {rows.length ? (
            rows.map(({ process, depth, hasChildren }) => {
              const risk = riskByPid.get(process.pid);
              const flagReason = (reasonsByPid.get(process.pid) || []).join("\n");
              const isCollapsed = !filtering && collapsed.has(process.pid);
              const detection = findProcessDetail({ process: process.name, pid: process.pid });
              return (
                <div
                  key={process.pid}
                  title={flagReason || undefined}
                  className={`thunter-process-tree__row thunter-process-tree__row--clickable ${
                    risk ? `thunter-process-tree__row--${risk}` : ""
                  } ${selectedPid === process.pid ? "thunter-process-tree__row--selected" : ""}`}
                  onClick={() => selectProcess(process.pid)}
                  onDoubleClick={() => hasChildren && toggleCollapsed(process.pid)}>
                  <span className="thunter-process-tree__name" style={{ paddingLeft: depth * 16 }}>
                    {hasChildren ? (
                      <button
                        type="button"
                        className="thunter-process-tree__caret"
                        disabled={filtering}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapsed(process.pid);
                        }}>
                        {isCollapsed ? "+" : "−"}
                      </button>
                    ) : (
                      <span className="thunter-process-tree__caret-space" />
                    )}
                    <span className="thunter-process-tree__icon" />
                    <span
                      title={
                        flagReason
                          ? `${flagReason}\n\n${process.cmdline || process.name}`
                          : process.cmdline || process.name
                      }>
                      {process.name}
                    </span>
                    {detection ? (
                      <DetailButton
                        title="Show detection details"
                        className="thunter-process-tree__details-button"
                        color="var(--color-blue)"
                        iconSize={10}
                        onClick={() => openDetail(detection)}
                      />
                    ) : null}
                  </span>
                  <span className="thunter-process-tree__right">{process.pid}</span>
                  <span className="thunter-process-tree__right">{process.threads ?? ""}</span>
                  <span title={process.user}>{process.user}</span>
                  <span>{process.description}</span>
                  <span>{process.company}</span>
                  <span title={process.path}>{process.path}</span>
                </div>
              );
            })
          ) : (
            <p className="thunter-process-tree__hint">No processes match the filter.</p>
          )}
        </div>

        <div
          className="thunter-process-tree__splitter"
          style={{ height: SPLITTER_HEIGHT }}
          onMouseDown={startResize}
          title="Drag to resize">
          <span className="thunter-process-tree__grip" />
        </div>

        <div className="thunter-process-tree__lower">
          <div className="thunter-process-tree__tabs-row">
            <div className="button-block thunter-process-tree__tabs">
              <TabButton title="Details" onClick={setDetailTab} active={detailTab} />
              <TabButton
                title="DLLs"
                tabDisplay={selected ? `DLLs (${selectedModules.length})` : "DLLs"}
                onClick={setDetailTab}
                active={detailTab}
              />
              <TabButton
                title="Network"
                tabDisplay={selected ? `Network (${selectedSockets.length})` : "Network"}
                onClick={setDetailTab}
                active={detailTab}
              />
            </div>
            {selected ? (
              <span className="thunter-process-tree__selected">
                {selected.name} ({selected.pid})
              </span>
            ) : null}
          </div>
          <div className="thunter-process-tree__lower-content">{renderDetailPane()}</div>
        </div>
      </div>

      {popup}
    </div>
  );
};

export default ProcessTree;
